import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { BILLING_ROUTES } from '@/api/constants/app.url';
import { cn } from '@/shared/utils/cn';
import { UpgradeContent, type UpgradeRequest } from '@/shared/access/upgrade-content';
import { AccessDeniedContent } from '@/shared/access/access-denied-content';

export type { UpgradeRequest };

/**
 * The shared "you don't have access → upgrade" popup. Presentational only — the
 * `UpgradeProvider` owns open state and mounts a single instance. CTA routes to
 * Billing with the plan picker opened.
 */
export function UpgradeModal({ request, onClose }: { request: UpgradeRequest; onClose: () => void }) {
    const navigate = useNavigate();

    const [shown, setShown] = useState(false);
    useEffect(() => {
        const id = requestAnimationFrame(() => setShown(true));
        return () => cancelAnimationFrame(id);
    }, []);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    const goToBilling = () => {
        onClose();
        navigate(BILLING_ROUTES.upgrade(request.feature));
    };

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="upgrade-modal-title"
            className={cn(
                'fixed inset-0 z-[1100] flex items-center justify-center p-4 transition-opacity duration-200',
                'bg-ink-900/50 backdrop-blur-sm',
                shown ? 'opacity-100' : 'opacity-0',
            )}
            onClick={onClose}
        >
            <div
                className={cn(
                    'relative mx-auto w-full transition-all duration-200 ease-out',
                    request.reason === 'permission' ? 'max-w-lg' : 'max-w-4xl',
                    shown ? 'translate-y-0 scale-100 opacity-100' : 'translate-y-2 scale-[0.98] opacity-0',
                )}
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close"
                    className="absolute right-4 top-4 z-10 inline-flex h-9 w-9 items-center justify-center rounded-full bg-white text-ink-500 shadow-sm transition-colors hover:bg-ink-50 hover:text-ink-700"
                >
                    <X className="h-4 w-4" />
                </button>

                <div id="upgrade-modal-title" className="sr-only">
                    {request.reason === 'permission' ? 'Access not available' : 'Upgrade plan'}
                </div>
                {/* A role block is not an upsell — the admin grants it, not billing. */}
                {request.reason === 'permission' ? (
                    <AccessDeniedContent feature={request.feature} variant="modal" onClose={onClose} />
                ) : (
                    <UpgradeContent
                        request={request}
                        variant="modal"
                        onUpgrade={goToBilling}
                        onDismiss={onClose}
                    />
                )}
            </div>
        </div>
    );
}
